// src/components/DetailPersonnel.js
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { apiFetch } from "../utils/wkCommon";
import { toPinyinSync, capitalizePinyin } from "../utils/pinyinUtil";
import { getUser } from "../utils/auth";

/**
 * DetailPersonnel (v3)
 * - same layout as before
 * - highlight (yellow) + red star only on the name(s) that match login user
 */

const css = `
.dp-card { background: #fff; border-radius: 0.5rem; padding: 0.75rem; box-shadow: 0 1px 0 rgba(16,32,58,0.04); border:1px solid rgba(15,23,42,0.04); font-size: 1rem; }
.dp-title { font-size: 0.9rem; font-weight:700; color:#0b4388; margin-bottom:0.5rem; }

/* layout: two-column like (role | content) */
.dp-row { gap: 0.75rem; align-items:flex-start; margin-bottom:0.5rem; padding-top: 12px; }
.dp-role { width: 7.5rem; min-width: 7.5rem; font-size:0.875rem; font-weight:700; color:#475569; }
.dp-content { flex:1; padding-left: 0.25rem; }
.dp-item { margin: 0.125rem 0; font-size:0.95rem; color:#0f1724; line-height:1.3; }
.dp-pinyin { margin: 0.125rem 0; font-size:0.95rem; color:#3f4754; line-height:1.3; font-style:italic; }

/* smaller muted text for loading/error */
.small-muted { font-size:0.8125rem; color:#6b7280; }

/* ordered list styles for personnel lines */
.dp-ol { list-style: none; margin: 0; padding: 0; }
.dp-li { display: flex; gap: 0.75rem; align-items:flex-start; margin: 0.25rem 0; }
.dp-index { min-width: 1.5rem; text-align: right; font-weight:700; color:#475569; font-size:0.875rem; }
.dp-li-body { flex:1; }

/* user-name highlight (same as CardAgenda): soft yellow + red star */
.user-name-highlight { background: #fff8df; padding: 0.125rem 0.35rem; border-radius: 0.25rem; display:inline-flex; align-items:center; gap:0.35rem; }
.user-name-star { color: #dc2626; font-weight:800; font-size:1rem; line-height:1; margin-right:0.25rem; }
`;

/* helpers */
function safeArray(m) {
  if (!m) return [];
  if (Array.isArray(m)) return m;
  if (m.data && Array.isArray(m.data)) return m.data;
  return [];
}
function normalize(o) {
  return o?.attributes ? o.attributes : o || {};
}

/* Name + id extraction helpers:
   Return array of objects: { name: string, ids: { id, documentId, umatID } }
*/
function namesFromUmatObjs(arr) {
  const out = [];
  safeArray(arr).forEach(uRaw => {
    const u = normalize(uRaw);
    // detect dianchuanshi flag (schema field: "dianchuanshi")
    const isDianChuanShi = Boolean(u.dianchuanshi);
    const chinese = (u.chineseName || "").trim();
    const full = (u.fullName || u.displayName || u.name || "").trim();

    // if chineseName exists and dianchuanshi true -> append 經理
    let chosen = null;
    if (chinese) {
      chosen = isDianChuanShi ? `${chinese}經理` : chinese;
    } else if (full) {
      // do not append 經理 to latin/full name per request
      chosen = full;
    } else {
      chosen = null;
    }
    
    if (!chosen) return;
    const ids = {
      id: u.id ?? u.documentId ?? u.$id ?? null,
    };
    out.push({ name: chosen, ids });
  });
  return out;
}

function namesFromExternal(eArr) {
  const out = [];
  safeArray(eArr).forEach(xRaw => {
    const x = normalize(xRaw);
    const chinese = (x.chineseName || "").trim();
    const full = (x.fullName || x.displayName || x.name || "").trim();
    const chosen = chinese || full || null;
    if (!chosen) return;
    // external people usually don't have internal umat ids - keep ids null
    out.push({ name: chosen, ids: { id: null, documentId: null, umatID: null } });
  });
  return out;
}

function namesFromOriginalNames(origStr) {
  const out = [];
  if (!origStr || typeof origStr !== "string") return out;
  const tokens = origStr.split(/[,\n;]+/).map(s => s.trim()).filter(Boolean);
  tokens.forEach(t => out.push({ name: t, ids: { id: null, documentId: null, umatID: null } }));
  return out;
}

/* Combine unique names preserving order; keep ids where available */
function gatherNamesWithIds(entry) {
  const out = [];

  (entry?.InternalUmat || []).forEach(u => {
    const name = (u.name_zh || u.name_id || "").trim();
    if (!name) return;
    out.push({
      name,
      ids: { documentId: u.refId || null }
    });
  });

  (entry?.ExternalUmat || []).forEach(u => {
    const name = (u.name_zh || u.name_id || "").trim();
    if (!name) return;
    out.push({
      name,
      ids: { documentId: u.refId || null }
    });
  });

  (entry?.ExternalNonUmat || []).forEach(u => {
    const name = (u.name_zh || u.name_id || "").trim();
    if (!name) return;
    out.push({
      name,
      ids: { documentId: null }
    });
  });

  return out;
}

/* Role label mapping — customize here */
const roleLabelMap = {
  Caochi: "操持 Caochi",
  Shengge: "聖歌 Shengge",
  Penceramah: "題目 Topik"
};

/* check if provided ids match the logged-in user */
function isIdsMatchingUser(ids, userUmat) {
  if (!userUmat || !ids) return false;
  const userId = userUmat.documentId ?? userUmat.id ?? null;
  const userDoc = userUmat.documentId ?? userUmat.$id ?? null;
  const userUmatID = userUmat.umatID ?? userUmat.kodeUmat ?? null;

  if (ids.id && userId && String(ids.id) === String(userId)) return true;
  if (ids.documentId && userDoc && String(ids.documentId) === String(userDoc)) return true;
  if (ids.umatID && userUmatID && String(ids.umatID) === String(userUmatID)) return true;
  return false;
}

export default function DetailPersonnel({ selectedAgenda }) {
  const [personnels, setPersonnels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // get login user once
  const loginUser = getUser?.();
  const loginUmat = loginUser?.umat ?? null;

  // extract documentId (agenda id) from selectedAgenda (support attributes wrapper)
  const agendaDocId = selectedAgenda
    ? (selectedAgenda.documentId || (selectedAgenda.attributes && selectedAgenda.attributes.documentId) || selectedAgenda.id || (selectedAgenda.attributes && selectedAgenda.attributes.id) || null)
    : null;

  useEffect(() => {
    let mounted = true;
    async function load() {
      setErr(null);
      setPersonnels(null);

      if (!selectedAgenda) return;
      // check possible places for pre-populated relation
      const src = selectedAgenda.attributes ? selectedAgenda.attributes : selectedAgenda;
      const pre = src?.agenda_book?.agendaPersonnels ?? null;
      const preArr = pre ? [pre] : [];

      if (preArr.length) {
        // use prepopulated
        if (mounted) setPersonnels(preArr);
        return;
      }

      // if no prepopulated, query Strapi by agenda.documentId
      if (!agendaDocId) {
        if (mounted) setPersonnels([]);
        return;
      }

      setLoading(true);
      try {
        // Lazy query: only for this selected agenda.
        // Populate minimal relations needed for names.
        const path = `/api/agenda-personnels?filters%5Bagenda%5D%5BdocumentId%5D%5B$eq%5D=${encodeURIComponent(agendaDocId)}&populate[0]=umats&populate[1]=external_people`;
        const res = await apiFetch(path);
        const items = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : []);
        if (mounted) setPersonnels(items);
      } catch (e) {
        console.error("DetailPersonnel apiFetch error", e);
        if (mounted) setErr(e.message || String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    //console.log("load")
    load();
    return () => { mounted = false; };
  }, [selectedAgenda, agendaDocId]);

  if (!selectedAgenda) return null;

  if (loading) {
    return (
      <div style={{ marginTop: "0.5rem" }}>
        <div className="dp-card" style={{ padding: "0.5rem" }}>
          <style dangerouslySetInnerHTML={{ __html: css }} />
          <div className="small-muted">Memuat...</div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div style={{ marginTop: "0.5rem" }}>
        <div className="dp-card">
          <style dangerouslySetInnerHTML={{ __html: css }} />
          <div className="small-muted">Gagal memuat petugas: {err}</div>
        </div>
      </div>
    );
  }

  const arr = safeArray(personnels);
  if (!arr.length) return null;

  // normalize entries to attributes object (Strapi may return { id, attributes })
  const normalized = arr;

  // desired roles in order
  const rolesOrder = ["Caochi", "Shengge", "Penceramah"];

  // build grouped blocks
  const grouped = rolesOrder.map(role => {
    const items = normalized
      .flatMap(g => g?.GroupRole || [])
      .flatMap(gr => gr?.ServiceRole || [])
      .filter(r => r?.Label_id === role);

    if (!items.length) return null;

    // sort by seqno if present
    items.sort((a, b) => {
      const sa = typeof a.seqno === "number" ? a.seqno : (a.seqNo ?? 999999);
      const sb = typeof b.seqno === "number" ? b.seqno : (b.seqNo ?? 999999);
      return sa - sb;
    });

    if (role === "Caochi") {
      // collect names across items, unique, but now keep ids
      const namesWithIds = [];
      items.forEach(it => {
        const ns = gatherNamesWithIds(it);
        ns.forEach(nobj => {
          if (!namesWithIds.some(x => x.name === nobj.name)) namesWithIds.push(nobj);
        });
      });
      if (!namesWithIds.length) return null;
      return { role, lines: [namesWithIds] }; // lines: array where each line is array of {name, ids}
    } else {
      // Shengge & Penceramah: Subject — Names (names comma-separated)
      const lines = [];
      items.forEach(it => {
        const subj = (it.subject || "").trim();
        const ns = gatherNamesWithIds(it);
        if (!ns.length && !subj) return;
        // each line keep array of name objs
        lines.push({ subject: subj, namesWithIds: !ns.length ? [] : ns });
      });
      if (!lines.length) return null;
      return { role, lines };
    }
  }).filter(Boolean);

  if (!grouped.length) return null;

  // small helper to render a list of name objects joined by comma, but with user highlighting
  function renderNamesWithHighlight(nameObjs) {
    // nameObjs: [{name, ids}, ...]
    return nameObjs.map((no, idx) => {
      const match = isIdsMatchingUser(no.ids, loginUmat);
      const sep = idx < nameObjs.length - 1 ? ", " : "";
      if (match) {
        return (
          <React.Fragment key={`${no.name}-${idx}`}>
            <span className="user-name-highlight" style={{ fontSize: "1rem" }}>
              <span>{no.name}</span>
              <span className="user-name-star" aria-hidden="true">*</span>
            </span>
            {sep}
          </React.Fragment>
        );
      }
      return <React.Fragment key={`${no.name}-${idx}`}>{no.name}{sep}</React.Fragment>;
    });
  }

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <div className="dp-card" aria-label="Petugas utama">
        <style dangerouslySetInnerHTML={{ __html: css }} />
        {grouped.map(g => (
          <div key={g.role} className="dp-row">
            <div className="dp-title">{roleLabelMap[g.role] || g.role}</div>
            <div className="dp-content">
              {g.role === "Caochi" ? (
                // Caochi: single line with possibly multiple names
                g.lines.map((nameArr, idx) => {
                  // nameArr is array of {name, ids}
                  let pinyin = "";
                  try {
                    // use joined plain names for pinyin generation (no harm)
                    const plain = nameArr.map(n => n.name).join(", ");
                    const raw = toPinyinSync(plain);
                    pinyin = capitalizePinyin(raw || "");
                  } catch (e) {
                    pinyin = "";
                  }
                  return (
                    <div className="dp-item" key={idx}>
                      <div>{renderNamesWithHighlight(nameArr)}</div>
                      {pinyin ? <div className="dp-pinyin" aria-hidden="true">{pinyin}</div> : null}
                    </div>
                  );
                })
              ) : (
                // Shengge & Penceramah: multiple lines; render as numbered list if more than one line
                g.lines.length > 1 ? (
                  <ol className="dp-ol" style={{ counterReset: "item" }}>
                    {g.lines.map((ln, idx) => {
                      let pinyin = "";
                      try {
                        const plain = ((ln.subject || "").trim() ? (ln.subject + " — ") : "") + ln.namesWithIds.map(n => n.name).join(", ");
                        const raw = toPinyinSync(plain);
                        pinyin = capitalizePinyin(raw || "");
                      } catch (e) {
                        pinyin = "";
                      }

                      return (
                        <li className="dp-li" key={idx} style={{ listStyleType: "none" }}>
                          <div className="dp-index" aria-hidden="true" style={{ marginRight: "0.5rem", fontSize: "0.95rem" }}>
                            {idx + 1}.
                          </div>
                          <div className="dp-li-body">
                            <div className="dp-item">
                              <div>{ln.subject ? <strong>{ln.subject} — </strong> : null}{renderNamesWithHighlight(ln.namesWithIds)}</div>
                              {pinyin ? <div className="dp-pinyin" aria-hidden="true">{pinyin}</div> : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  // single line: keep previous inline style
                  g.lines.map((ln, idx) => {
                    let pinyin = "";
                    try {
                      const plain = ((ln.subject || "").trim() ? (ln.subject + " — ") : "") + ln.namesWithIds.map(n => n.name).join(", ");
                      const raw = toPinyinSync(plain);
                      pinyin = capitalizePinyin(raw || "");
                    } catch (e) {
                      pinyin = "";
                    }
                    return (
                      <div className="dp-item" key={idx}>
                        <div>{ln.subject ? <strong>{ln.subject} — </strong> : null}{renderNamesWithHighlight(ln.namesWithIds)}</div>
                        {pinyin ? <div className="dp-pinyin" aria-hidden="true">{pinyin}</div> : null}
                      </div>
                    );
                  })
                )
              )}
            </div>
          </div>
        ))}

      </div>
    </div>
  );
}

DetailPersonnel.propTypes = {
  selectedAgenda: PropTypes.oneOfType([PropTypes.object, PropTypes.any])
};
